import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Typography, Grid, CircularProgress, Paper } from "@mui/material";
// import { queryGoogleCalendar } from "../api/actions/query"; // Deprecated: inconsistent data
import { getAppointmentMetrics } from "../../user/api/actions/query"; // Use consistent CRM metrics
import { store } from "../../../setup/store"; // Import store to dispatch action
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import {
    Today as TodayIcon,
    CheckCircle as SuccessIcon,
    Paid as PaidIcon,
    PendingActions as PendingIcon,
    Error as FailedIcon,
    Public as OnlineIcon,
    MoneyOff as FreeIcon
} from "@mui/icons-material";

dayjs.extend(isBetween);

const AppointmentReport = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalBooking: 0,
        upcomingEvents: 0,
        totalPayment: 0
    });

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            // Dispatch the action to execute the thunk and get the promise result
            const response = await store.dispatch(getAppointmentMetrics());
            // response.data is the JSON body. data.data.appointments is the list.
            if (response && response.data && response.data.success) {
                const currencySymbol = response.data.data?.currencySymbol || '$';
                calculateStats(response.data.data.appointments || [], currencySymbol);
            }
        } catch (error) {
            console.error("Error fetching calendar events for report:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (events, currencySymbol = '$') => {
        const now = dayjs(); // Current time

        let totalBooking = 0;
        let upcomingEvents = 0;
        let totalPayment = 0;

        events.forEach(event => {
            const paymentStatus = event.paymentStatus;
            const appointmentStatus = event.appointmentStatus;
            const grandTotal = Number(event.grandTotal) || 0;

            // Date parsing - use scheduledDate for "Upcoming Event" count
            // CRM data usually has scheduledDate or start.dateTime
            const scheduledDateVal = event.scheduledDate || (event.start && (event.start.dateTime || event.start.date));
            const scheduledDate = dayjs(scheduledDateVal);

            // Check if scheduled date is valid and is same or after now (future or present)
            const isUpcoming = scheduledDate.isValid() && (scheduledDate.isAfter(now) || scheduledDate.isSame(now, 'minute'));

            // Condition for Booking Count: status is 'completed' or 'scheduled'
            const isValidBooking = appointmentStatus === 'completed' || appointmentStatus === 'scheduled';

            // 1. Total Booking
            if (isValidBooking) {
                totalBooking++;
            }

            // 2. Upcoming Event (Scheduled/Completed status AND date is not passed)
            // "schedule date is not passed count current and future date count"
            if (isUpcoming && isValidBooking) {
                upcomingEvents++;
            }

            // 3. Payment Card: Total successful paid amount
            // "total successfull paid amount total"
            // Assumption: successful paid means paymentStatus is 'completed'
            if (paymentStatus === 'completed') {
                totalPayment += grandTotal;
            }
        });

        setStats({
            totalBooking,
            upcomingEvents,
            totalPayment,
            currencySymbol
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={1} wrap="nowrap" sx={{
                overflowX: 'auto',
                pb: 0.5,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                    display: 'none'
                }
            }}>
                <Grid item sx={{ width: 300 }}>
                    <SummaryCard title="Total Booking" value={stats.totalBooking} icon={<TodayIcon color="primary" fontSize="small" />} />
                </Grid>
                <Grid item sx={{ width: 300 }}>
                    <SummaryCard title="Upcoming Events" value={stats.upcomingEvents} icon={<TodayIcon color="secondary" fontSize="small" />} />
                </Grid>
                <Grid item sx={{ width: 300 }}>
                    <SummaryCard title="Total Payments" value={`${stats.currencySymbol || '$'} ${stats.totalPayment.toFixed(2)}`} icon={<PaidIcon color="success" fontSize="small" />} />
                </Grid>
            </Grid>
        </Box>
    );
};

const SummaryCard = ({ title, value, icon }) => (
    <Paper
        elevation={0}
        sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            backgroundColor: '#fff',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        }}
    >
        <Box sx={{ mb: 0.5, opacity: 0.8 }}>
            {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" align="center" sx={{ mb: 0, fontWeight: 500 }}>
            {title}
        </Typography>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
            {value}
        </Typography>
    </Paper>
);

export default AppointmentReport;
